import React, { useRef } from "react"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../../shared/components/pageHeader/PageHeaderApi"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faShop } from "@fortawesome/pro-light-svg-icons"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"
import { SUPPORT_ENTITIES_ROUTE, SUPPORT_ROUTE } from "../../../../../../constants/routes"
import { FormikProps } from "formik"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { enqueueSnackbar } from "notistack"
import { createCompanyApi } from "../createCompanyApi"
import { Button } from "../../../../../office/admin/components/Button"
import { useNavigate } from "react-router"
import { CompanyFormValues } from "../companyTypes"
import { CompanyCreateUpdateForm } from "../components/CompanyCreateUpdateForm"
import { AgreementOptions, BusinessType, EntityType } from "../../supportEntitiesTypes"
import { getAddress, getContactDetails, getInvoiceDetails } from "../../helpers"
import { SupportEntityQuery } from "../../supportEntityApi"

export function CreateCompany() {
    const navigate = useNavigate()
    const companyFormRef = useRef<FormikProps<CompanyFormValues>>(null)

    const { mutate } = useApiMutation(createCompanyApi, {
        async onSuccess(_, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [SupportEntityQuery.LIST] })
            navigate(SUPPORT_ENTITIES_ROUTE)
            if (data?.serviceResponse?.displayMessage) {
                enqueueSnackbar({
                    variant: "success",
                    message: data?.serviceResponse?.displayMessage,
                })
            }
        },
    })

    const createCompany = (value: CompanyFormValues) => {
        mutate({
            entity: {
                isSystemOwner: value.isSystemOwner === AgreementOptions.YES,
                isOfficeHotel: value.isOfficeHotel,
                businessType: value.businessType as BusinessType,
                businessNumber: value.businessNumber,
                mainEanNumber: value.mainEanNumber,
                name: value.name,
                type: EntityType.COMPANY,
                address: getAddress(value),
                contactDetails: getContactDetails(value),
                ...getInvoiceDetails(value),
                ...(value.isSelfSignUp && {
                    signupDetails: {
                        selfSignup: {
                            urlIdentifier: value.urlIdentifier,
                        },
                    },
                }),
                isActive: value.isActive,
            },
        })
    }

    const handleSaveClick = () => {
        companyFormRef.current?.submitForm()
    }

    return (
        <>
            <LocalizedStrict id="support-create-company-title" attrs={{ title: true }}>
                <PageHeader
                    page={Page.SUPPORT_CREATE_COMPANY}
                    title="Create company"
                    icon={<FontAwesomeIcon icon={faShop} />}
                    rightElement={
                        <LocalizedStrict id="support-create-company-create-button">
                            <Button variant="contained" onClick={handleSaveClick}>
                                Create
                            </Button>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="support-create-company-nav-item-home">
                            <BreadcrumbLink to={SUPPORT_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="support-create-company-nav-item-entities">
                            <BreadcrumbLink to={SUPPORT_ENTITIES_ROUTE}>Entities</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="support-create-company-nav-item-create-company">
                            <Breadcrumb>Create company</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>
            <CompanyCreateUpdateForm ref={companyFormRef} onSubmit={createCompany} />
        </>
    )
}
